(() => {
  const root = document.querySelector('[data-lc-cart-root]');
  if (!root || root.dataset.lcCartReady) return;
  root.dataset.lcCartReady = 'true';

  const dialog = root.querySelector('[data-lc-cart-dialog]');
  const sectionId = root.dataset.sectionId;
  const strings = JSON.parse(root.querySelector('[data-lc-cart-strings]').textContent);
  const cartError = root.querySelector('[data-lc-cart-error]');
  const live = root.querySelector('[data-lc-cart-live]');
  const endpoint = (name) => `${root.dataset[name].replace(/\.js$/, '')}.js`;
  const sectionContext = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    return `${url.pathname}${url.search}`;
  };
  let queue = Promise.resolve();
  let busy = 0;
  let returnFocus = null;
  let noteTimer = null;
  let noteDraft = root.querySelector('[data-lc-cart-note]')?.value || '';
  let noteDirty = false;
  let checkoutPending = false;

  function enqueue(action) {
    const result = queue.then(action);
    queue = result.catch(() => {});
    return result;
  }

  function announce(message) {
    live.textContent = '';
    requestAnimationFrame(() => { live.textContent = message; });
  }

  function showError(error, form, { focus = false } = {}) {
    const message = error?.message || strings.error;
    const formError = form?.querySelector('[data-lc-form-error]');
    if (formError) {
      formError.textContent = message;
      formError.hidden = false;
    }
    cartError.textContent = message;
    cartError.hidden = false;
    announce(message);
    if (focus) {
      if (dialog.open) cartError.focus({ preventScroll: true });
      else if (formError) {
        formError.focus({ preventScroll: true });
        formError.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
    }
  }

  function clearError(form) {
    cartError.hidden = true;
    cartError.textContent = '';
    const formError = form?.querySelector('[data-lc-form-error]');
    if (formError) {
      formError.hidden = true;
      formError.textContent = '';
    }
  }

  function setBusy(value) {
    busy = Math.max(0, busy + (value ? 1 : -1));
    const content = root.querySelector('[data-lc-cart-content]');
    content?.setAttribute('aria-busy', String(busy > 0));
    if (busy > 0) {
      content?.querySelectorAll('[data-lc-cart-step], [data-lc-cart-quantity], [name="checkout"]').forEach((control) => {
        if (!control.disabled) {
          control.dataset.lcDisabledForRequest = 'true';
          control.disabled = true;
        }
      });
    } else {
      content?.querySelectorAll('[data-lc-disabled-for-request]').forEach((control) => {
        control.disabled = false;
        delete control.dataset.lcDisabledForRequest;
      });
    }
  }

  async function request(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', ...options.headers }
    });
    let data;
    try { data = await response.json(); } catch { throw new Error(strings.error); }
    if (!response.ok || data.status >= 400) {
      const details = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : strings.error;
      throw new Error(details);
    }
    return data;
  }

  function updateCounts(count) {
    document.querySelectorAll('[data-lc-cart-count]').forEach((element) => { element.textContent = String(count); });
  }

  function renderContent(html) {
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('[data-lc-cart-content]');
    const current = root.querySelector('[data-lc-cart-content]');
    if (!replacement || !current) return false;
    const disclosureOpen = !!current.querySelector('[data-lc-cart-note-details]')?.open;
    const scrollTop = current.querySelector('.lc-cart__scroll')?.scrollTop || 0;
    const focusInside = current.contains(document.activeElement);
    const activeLineKey = document.activeElement?.closest('[data-lc-cart-line]')?.dataset.lcCartLine;
    const activeWasQuantity = document.activeElement?.matches('[data-lc-cart-quantity]');
    const activeWasNote = document.activeElement?.matches('[data-lc-cart-note]');
    const noteSelection = activeWasNote ? [document.activeElement.selectionStart, document.activeElement.selectionEnd] : null;

    current.replaceWith(replacement);
    const note = replacement.querySelector('[data-lc-cart-note]');
    if (note) {
      if (noteDirty) note.value = noteDraft;
      else noteDraft = note.value;
    }
    const disclosure = replacement.querySelector('[data-lc-cart-note-details]');
    if (disclosure && disclosureOpen) disclosure.open = true;
    const scroll = replacement.querySelector('.lc-cart__scroll');
    if (scroll) scroll.scrollTop = scrollTop;
    updateCounts(Number(replacement.dataset.cartCount || 0));

    if (focusInside && dialog.open) {
      if (activeWasNote && note) {
        note.focus({ preventScroll: true });
        if (noteSelection) note.setSelectionRange(...noteSelection);
      } else if (activeWasQuantity && activeLineKey) {
        const input = replacement.querySelector(`[data-lc-cart-line="${CSS.escape(activeLineKey)}"] [data-lc-cart-quantity]`);
        (input || root.querySelector('[data-lc-cart-close]')).focus({ preventScroll: true });
      } else root.querySelector('[data-lc-cart-close]').focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('lc:cart-updated', { detail: { itemCount: Number(replacement.dataset.cartCount || 0) } }));
    return true;
  }

  async function refresh() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' }, cache: 'no-store' });
    if (!response.ok || !renderContent(await response.text())) throw new Error(strings.error);
  }

  async function renderResponse(data) {
    if (!renderContent(data.sections?.[sectionId])) await refresh();
  }

  function open(trigger, shouldRefresh = true) {
    if (!dialog.open) {
      returnFocus = trigger || document.activeElement;
      dialog.showModal();
      document.documentElement.classList.add('lc-cart-is-open');
      document.dispatchEvent(new CustomEvent('lc:cart-open'));
    }
    if (shouldRefresh) {
      enqueue(async () => {
        setBusy(true);
        try { await refresh(); } finally { setBusy(false); }
      }).catch((error) => showError(error));
    }
  }

  function close() {
    if (dialog.open) dialog.close();
  }

  async function saveNote() {
    if (!noteDirty) return;
    const value = noteDraft;
    await request(endpoint('updateUrl'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: value })
    });
    if (noteDraft === value) {
      noteDirty = false;
      const status = root.querySelector('[data-lc-note-status]');
      if (status) status.textContent = strings.note_saved;
    }
  }

  function scheduleNote() {
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      noteTimer = null;
      enqueue(saveNote).catch((error) => showError(error));
    }, 550);
  }

  async function changeLine(key, quantity) {
    setBusy(true);
    clearError();
    try {
      const data = await request(endpoint('changeUrl'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [sectionId], sections_url: sectionContext() })
      });
      await renderResponse(data);
      announce(strings.updated);
    } catch (error) {
      try { await refresh(); } catch {}
      showError(error);
    } finally { setBusy(false); }
  }

  async function addProduct(form, submitter, body) {
    const submit = submitter || form.querySelector('[data-lc-submit], [type="submit"], button[name="add"]');
    const variantBefore = body.get('id');
    const wasDisabled = submit?.disabled;
    let added = false;
    clearError(form);
    form.dataset.lcAdding = 'true';
    form.setAttribute('aria-busy', 'true');
    if (submit) {
      submit.dataset.lcCartLoading = 'true';
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
    }
    announce(strings.adding);

    try {
      body.delete('return_to');
      body.set('sections', sectionId);
      body.set('sections_url', sectionContext());
      const result = await request(endpoint('addUrl'), { method: 'POST', body });
      added = true;
      open(submit || form, false);
      setBusy(true);
      try { await renderResponse(result); } finally { setBusy(false); }
      announce(strings.added);
    } catch (error) {
      if (!added) {
        // An inventory error can still add the available stock. Read the actual
        // cart before presenting the error so quantities and counts stay correct.
        try {
          await refresh();
          if (Number(root.querySelector('[data-lc-cart-content]')?.dataset.cartCount || 0) > 0) open(submit || form, false);
        } catch {}
      }
      showError(added ? new Error(strings.added_refresh_error) : error, form, { focus: true });
    } finally {
      delete form.dataset.lcAdding;
      form.removeAttribute('aria-busy');
      if (submit) {
        delete submit.dataset.lcCartLoading;
        submit.removeAttribute('aria-busy');
        if (new FormData(form).get('id') === variantBefore) submit.disabled = !!wasDisabled;
      }
      form.dispatchEvent(new CustomEvent('lc:product-form-ready', { bubbles: true }));
    }
  }

  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('lc-cart-is-open');
    document.dispatchEvent(new CustomEvent('lc:cart-close'));
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    if (noteDirty) {
      clearTimeout(noteTimer);
      noteTimer = null;
      enqueue(saveNote).catch((error) => showError(error));
    }
  });

  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
  });

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-lc-cart-open], [data-lc-cart-close], [data-lc-cart-remove], [data-lc-cart-step]');
    if (!target) return;
    if (target.matches('[data-lc-cart-open]')) {
      event.preventDefault();
      clearError();
      open(target);
      return;
    }
    if (!root.contains(target)) return;
    event.preventDefault();
    if (target.matches('[data-lc-cart-close]')) { close(); return; }
    if (busy || checkoutPending) return;
    const line = target.closest('[data-lc-cart-line]');
    if (!line) return;
    if (target.matches('[data-lc-cart-remove]')) {
      enqueue(() => changeLine(line.dataset.lcCartLine, 0));
      return;
    }
    const input = line.querySelector('[data-lc-cart-quantity]');
    if (!input || target.disabled) return;
    const step = Number(input.step || 1);
    const quantity = Number(input.value) + Number(target.dataset.lcCartStep) * step;
    if (quantity < Number(input.min || 1) || (input.max && quantity > Number(input.max))) return;
    enqueue(() => changeLine(line.dataset.lcCartLine, quantity));
  });

  root.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.matches('[data-lc-cart-quantity]')) return;
    if (input.value.trim() === '') {
      input.value = input.dataset.previousQuantity;
      return;
    }
    if (busy || checkoutPending || !input.checkValidity()) {
      input.reportValidity();
      input.value = input.dataset.previousQuantity;
      return;
    }
    const quantity = Number(input.value);
    if (quantity === Number(input.dataset.previousQuantity)) return;
    const key = input.closest('[data-lc-cart-line]').dataset.lcCartLine;
    enqueue(() => changeLine(key, quantity));
  });

  root.addEventListener('input', (event) => {
    if (!event.target.matches('[data-lc-cart-note]')) return;
    noteDraft = event.target.value;
    noteDirty = true;
    const status = root.querySelector('[data-lc-note-status]');
    if (status) status.textContent = '';
    scheduleNote();
  });

  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-lc-cart-checkout]')) {
      if (form.dataset.lcNativeSubmit === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (checkoutPending) return;
      checkoutPending = true;
      clearTimeout(noteTimer);
      noteTimer = null;
      const note = root.querySelector('[data-lc-cart-note]');
      if (note && note.value !== noteDraft) { noteDraft = note.value; noteDirty = true; }
      enqueue(saveNote).then(() => {
        const currentForm = root.querySelector('[data-lc-cart-checkout]');
        if (!currentForm) { checkoutPending = false; return; }
        // Run after the original submit event finishes. An already-saved note
        // resolves synchronously; re-entering requestSubmit in that event is ignored.
        window.setTimeout(() => {
          if (!currentForm.isConnected) { checkoutPending = false; return; }
          currentForm.dataset.lcNativeSubmit = 'true';
          const currentNote = root.querySelector('[data-lc-cart-note]');
          if (currentNote) currentNote.value = noteDraft;
          const submit = currentForm.querySelector('[name="checkout"]');
          submit.disabled = false;
          currentForm.requestSubmit(submit);
        }, 0);
      }).catch((error) => { checkoutPending = false; showError(error); });
      return;
    }
    let isProductForm = form.hasAttribute('data-lc-product-form');
    if (!isProductForm) {
      try {
        const url = new URL(form.action, window.location.href);
        isProductForm = url.origin === window.location.origin && /\/cart\/add(?:\.js)?\/?$/.test(url.pathname);
      } catch {}
    }
    if (!isProductForm) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.lcAdding || !form.reportValidity()) return;
    const body = new FormData(form);
    form.dataset.lcAdding = 'queued';
    enqueue(() => addProduct(form, event.submitter, body)).catch((error) => showError(error, form, { focus: true }));
  }, true);

  window.addEventListener('pageshow', () => {
    checkoutPending = false;
    root.querySelector('[data-lc-cart-checkout]')?.removeAttribute('data-lc-native-submit');
  });

  window.LelloCleanCart = { open: (trigger, shouldRefresh = true) => open(trigger, shouldRefresh), close, refresh: () => enqueue(refresh) };
})();
